import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { forgotPassword, login } from "../../Slices/auth";

function LoginMui() {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [passwordScreen, setPasswordScreen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(5 * 60);
  const [alertMessage, setAlertMessage] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: "",
      otp: "",
      newPassword: "",
      renewpassword: "",
    },
  });

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeRemaining((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const loginApi = (data) => {
    dispatch(login(data))
      .unwrap()
      .then((data) => {
        if (data.success) {
          setAlertMessage("Logged in successfully.");
          localStorage.setItem("token", data.token);
          localStorage.setItem("username", data.user?.username);
          localStorage.setItem("userid", data.user?.id);
          localStorage.setItem("roles", JSON.stringify(data.user?.roles));
          localStorage.setItem("role", data.user?.default_role);

          // Navigate to hotel page after successful login
          navigate("/hotel");
        } else {
          toast.error(data.message || "Login failed");
        }
      })
      .catch((error) => {
        console.error("Login error:", error);
        toast.error("An error occurred during login.");
      });
  };

  const signinonSubmit = (formData) => {
    if (formData) {
      loginApi(formData);
    }
  };

  const forgotPasswordApi = (data) => {
    dispatch(forgotPassword(data))
      .unwrap()
      .then(() => {
        toast.success("Password changed successfully.");
        setPasswordScreen(false);
      })
      .catch(() => {
        toast.error("An error occurred during changing password.");
      });
  };

  const forgotonSubmit = (formData) => {
    if (formData) {
      forgotPasswordApi(formData);
    }
  };

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const toggleScreen = async () => {
    const isValid = await trigger("email");
    if (!isValid) {
      return;
    }
    setPasswordScreen(true);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        padding: "20px",
      }}
    >
      {passwordScreen ? (
        <Box
          sx={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="h5"
                sx={{ mb: 2, fontWeight: "bold", color: "#1a237e" }}
              >
                Reset Password
              </Typography>
              <Typography>
                OTP has been sent to your registered mobile number ******4342
              </Typography>
              <form onSubmit={handleSubmit(forgotonSubmit)}>
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="otp"
                    control={control}
                    rules={{ required: "OTP is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Enter OTP"
                        error={!!errors.otp}
                        helperText={errors.otp?.message}
                      />
                    )}
                  />
                  <Typography sx={{ mt: 1 }}>
                    Time remaining: {formatTime(timeRemaining)}
                  </Typography>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2 }}
                  >
                    Verify OTP
                  </Button>
                </Box>
              </form>
            </Grid>
          </Grid>
        </Box>
      ) : (
        <Box
          sx={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            width: "100%",
            maxWidth: "400px",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant="h5"
                sx={{ mb: 3, fontWeight: "bold", color: "#1a237e" }}
              >
                Welcome to Esthell
              </Typography>
              <form onSubmit={handleSubmit(signinonSubmit)}>
                <Box sx={{ mt: 2 }}>
                  <Controller
                    name="email"
                    control={control}
                    rules={{
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email"
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    )}
                  />
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: "Password is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        type={isPasswordVisible ? "text" : "password"}
                        label="Password"
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        sx={{ mt: 2 }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton onClick={togglePasswordVisibility}>
                                {isPasswordVisible ? (
                                  <VisibilityOff />
                                ) : (
                                  <Visibility />
                                )}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <FormControlLabel
                      control={<Checkbox />}
                      label="Remember me"
                    />
                    <Button
                      onClick={toggleScreen}
                      sx={{ textTransform: "none" }}
                    >
                      Forgot Password?
                    </Button>
                  </Box>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2 }}
                  >
                    Sign In
                  </Button>
                </Box>
              </form>
            </Grid>
          </Grid>
        </Box>
      )}
      <Snackbar
        open={!!alertMessage}
        autoHideDuration={3000}
        onClose={() => setAlertMessage(null)}
        message={alertMessage}
      />
    </Box>
  );
}

export default LoginMui;
